import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

export interface JobBillingWorkflowStatusLike {
  key?: string | null;
  label?: string | null;
  allow_billing?: boolean;
}

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const HARD_BLOCKED_JOB_BILLING_STATUS_KEYS = new Set(['scheduled']);
/** Only 'closed' makes a job's billing ledger read-only — all other statuses remain editable */
const READ_ONLY_JOB_BILLING_STATUS_KEYS = new Set(['closed']);

// System statuses that should NOT be auto-hard-blocked even though they are
// is_system=true and is_terminal=false. These statuses have their own billing
// semantics (e.g. billing_review allows billing, billing_submitted allows progress billing).
const SYSTEM_STATUS_BILLING_EXCEPTIONS = new Set(['billing_review', 'billing_submitted']);

export function isHardBlockedJobBillingStatus(statusKey?: string | null): boolean {
  const normalized = normalizeWorkflowStatusKey(statusKey);
  const configLoader = getWorkItemTypeRegistry();
  const isSystem = configLoader.isSystemStatus('job', normalized);
  const isTerminal = configLoader.isTerminalStatus('job', normalized);
  if (isSystem && !isTerminal && !SYSTEM_STATUS_BILLING_EXCEPTIONS.has(normalized)) return true;
  return HARD_BLOCKED_JOB_BILLING_STATUS_KEYS.has(normalized);
}

export function isReadOnlyJobBillingStatus(statusKey?: string | null): boolean {
  const normalized = normalizeWorkflowStatusKey(statusKey);
  // Only 'closed' makes a job read-only for billing — all other statuses (including
  // terminal ones like 'resolved', 'completed', 'billing_submitted') remain editable
  // to support progress billing and corrections.
  return READ_ONLY_JOB_BILLING_STATUS_KEYS.has(normalized);
}

export function isJobBillingEnabledForWorkflowStatus(status: JobBillingWorkflowStatusLike | null | undefined): boolean {
  if (!status) {
    return true;
  }

  if (isHardBlockedJobBillingStatus(status.key)) {
    return false;
  }

  // For read-only statuses (only 'closed'), respect explicit allow_billing flag
  if (isReadOnlyJobBillingStatus(status.key)) {
    return status.allow_billing === true;
  }

  return status.allow_billing !== false;
}

export function canShowJobBillableLedgerForStatus(
  currentStatus?: string | null,
  workflowStatuses?: Array<JobBillingWorkflowStatusLike> | null,
): boolean {
  const normalizedCurrentStatus = normalizeWorkflowStatusKey(currentStatus);

  if (!normalizedCurrentStatus) {
    return true;
  }

  if (isHardBlockedJobBillingStatus(normalizedCurrentStatus)) {
    return false;
  }

  if (isReadOnlyJobBillingStatus(normalizedCurrentStatus)) {
    return true;
  }

  const matchedStatus = Array.isArray(workflowStatuses)
    ? workflowStatuses.find((status) => normalizeWorkflowStatusKey(status?.key) === normalizedCurrentStatus)
    : null;

  if (!matchedStatus) {
    return true;
  }

  return isJobBillingEnabledForWorkflowStatus(matchedStatus);
}

export function getAllowedJobBillingStatuses(
  workflowStatuses?: Array<JobBillingWorkflowStatusLike> | null,
): JobBillingWorkflowStatusLike[] {
  if (!Array.isArray(workflowStatuses)) {
    return [];
  }

  return workflowStatuses.filter((status) => isJobBillingEnabledForWorkflowStatus(status));
}
